import { useId, type ReactNode, type Ref } from 'react';
import { COURT_WIDTH, FULL_LENGTH, HALF_LENGTH, isLine, type DiagramObject, type LineObject } from '@hoopify/shared';

/**
 * A FIBA court drawn to scale in metres, with diagram objects on top.
 * Basket at the top for half court; full court adds the far end at the bottom.
 */

const PAD = 0.6;
export const courtLength = (court: 'half' | 'full') => (court === 'half' ? HALF_LENGTH : FULL_LENGTH);
export const viewBox = (court: 'half' | 'full') => `${-PAD} ${-PAD} ${COURT_WIDTH + PAD * 2} ${courtLength(court) + PAD * 2}`;

// FIBA measurements (metres)
const BASKET_Y = 1.575;
const KEY_HALF = 2.45;
const FT_Y = 5.8;
const CIRCLE_R = 1.8;
const THREE_R = 6.75;
const THREE_X = 0.9;
const THREE_Y = BASKET_Y + Math.sqrt(THREE_R ** 2 - (7.5 - THREE_X) ** 2);
const NO_CHARGE_R = 1.25;
const BOARD_Y = 1.2;
const MID = COURT_WIDTH / 2;

function EndMarkings({ flip, length }: { flip: boolean; length: number }) {
  // Draw one end with the baseline at y=0, then mirror it for the far end.
  const t = flip ? `translate(0 ${length}) scale(1 -1)` : undefined;
  return (
    <g transform={t}>
      <rect x={MID - KEY_HALF} y={0} width={KEY_HALF * 2} height={FT_Y} />
      {/* Free-throw circle: solid outside the key, dashed inside */}
      <path d={`M ${MID - CIRCLE_R} ${FT_Y} A ${CIRCLE_R} ${CIRCLE_R} 0 0 0 ${MID + CIRCLE_R} ${FT_Y}`} />
      <path d={`M ${MID - CIRCLE_R} ${FT_Y} A ${CIRCLE_R} ${CIRCLE_R} 0 0 1 ${MID + CIRCLE_R} ${FT_Y}`} strokeDasharray="0.3 0.25" />
      {/* Three-point line */}
      <path d={`M ${THREE_X} 0 L ${THREE_X} ${THREE_Y} A ${THREE_R} ${THREE_R} 0 0 0 ${COURT_WIDTH - THREE_X} ${THREE_Y} L ${COURT_WIDTH - THREE_X} 0`} />
      {/* No-charge semicircle */}
      <path d={`M ${MID - NO_CHARGE_R} ${BOARD_Y} L ${MID - NO_CHARGE_R} ${BASKET_Y} A ${NO_CHARGE_R} ${NO_CHARGE_R} 0 0 0 ${MID + NO_CHARGE_R} ${BASKET_Y} L ${MID + NO_CHARGE_R} ${BOARD_Y}`} />
      {/* Backboard and ring */}
      <line x1={MID - 0.9} y1={BOARD_Y} x2={MID + 0.9} y2={BOARD_Y} strokeWidth={0.1} />
      <line x1={MID} y1={BOARD_Y} x2={MID} y2={BASKET_Y - 0.225} />
      <circle cx={MID} cy={BASKET_Y} r={0.225} />
    </g>
  );
}

export function CourtLines({ court }: { court: 'half' | 'full' }) {
  const length = courtLength(court);
  return (
    <g fill="none" stroke="var(--court-line)" strokeWidth={0.06} strokeLinecap="round">
      <rect x={0} y={0} width={COURT_WIDTH} height={length} fill="var(--court-floor)" />
      <EndMarkings flip={false} length={length} />
      {court === 'full' ? (
        <>
          <line x1={0} y1={HALF_LENGTH} x2={COURT_WIDTH} y2={HALF_LENGTH} />
          <circle cx={MID} cy={HALF_LENGTH} r={CIRCLE_R} />
          <EndMarkings flip length={length} />
        </>
      ) : (
        <path d={`M ${MID - CIRCLE_R} ${HALF_LENGTH} A ${CIRCLE_R} ${CIRCLE_R} 0 0 1 ${MID + CIRCLE_R} ${HALF_LENGTH}`} />
      )}
    </g>
  );
}

// ---------- Lines ----------

type Pt = { x: number; y: number };

/** Catmull-Rom through the points, sampled every `step` metres-ish. */
function sample(points: Pt[], perSegment = 16): Pt[] {
  if (points.length < 3) return points;
  const out: Pt[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[i + 2] ?? p2;
    for (let s = 0; s < perSegment; s++) {
      const t = s / perSegment;
      const t2 = t * t;
      const t3 = t2 * t;
      out.push({
        x: 0.5 * (2 * p1.x + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
        y: 0.5 * (2 * p1.y + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
      });
    }
  }
  out.push(points[points.length - 1]!);
  return out;
}

const pathOf = (pts: Pt[]) => pts.map((p, i) => `${i ? 'L' : 'M'} ${p.x.toFixed(3)} ${p.y.toFixed(3)}`).join(' ');

/** Walks the curve and wiggles side to side, leaving the last bit straight for the arrowhead. */
function zigzag(pts: Pt[]): Pt[] {
  const dense = sample(pts, 24);
  const out: Pt[] = [dense[0]!];
  let travelled = 0;
  let total = 0;
  for (let i = 1; i < dense.length; i++) total += Math.hypot(dense[i]!.x - dense[i - 1]!.x, dense[i]!.y - dense[i - 1]!.y);
  let side = 1;
  let next = 0.3;
  for (let i = 1; i < dense.length; i++) {
    const a = dense[i - 1]!;
    const b = dense[i]!;
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    travelled += len;
    if (travelled >= next && travelled < total - 0.6 && len > 0) {
      const nx = -(b.y - a.y) / len;
      const ny = (b.x - a.x) / len;
      out.push({ x: b.x + nx * 0.18 * side, y: b.y + ny * 0.18 * side });
      side *= -1;
      next += 0.3;
    }
  }
  out.push(dense[dense.length - 1]!);
  return out;
}

function Line({ o, markerId }: { o: LineObject; markerId: string }) {
  const pts = o.points;
  const end = pts[pts.length - 1]!;
  if (o.kind === 'screen') {
    const curve = sample(pts);
    const prev = curve[curve.length - 2] ?? pts[0]!;
    const len = Math.hypot(end.x - prev.x, end.y - prev.y) || 1;
    const nx = (-(end.y - prev.y) / len) * 0.45;
    const ny = ((end.x - prev.x) / len) * 0.45;
    return (
      <g>
        <path d={pathOf(curve)} />
        <line x1={end.x - nx} y1={end.y - ny} x2={end.x + nx} y2={end.y + ny} strokeWidth={0.12} />
      </g>
    );
  }
  const d = o.kind === 'dribble' ? pathOf(zigzag(pts)) : pathOf(sample(pts));
  return <path d={d} strokeDasharray={o.kind === 'pass' ? '0.28 0.2' : undefined} markerEnd={`url(#${markerId})`} />;
}

// ---------- Objects ----------

export function DiagramObjects({ objects, selectedId, interactive, onObjectPointerDown }: {
  objects: DiagramObject[];
  selectedId?: string | null;
  interactive?: boolean;
  onObjectPointerDown?: (id: string, e: React.PointerEvent) => void;
}) {
  const markerId = useId().replace(/:/g, '');
  const lines = objects.filter(isLine);
  const others = objects.filter((o): o is Exclude<DiagramObject, LineObject> => !isLine(o));
  const handlers = (id: string) =>
    interactive ? { onPointerDown: (e: React.PointerEvent) => onObjectPointerDown?.(id, e), style: { cursor: 'move' } as const } : { pointerEvents: 'none' as const };

  return (
    <>
      <defs>
        <marker id={markerId} viewBox="0 0 10 10" refX="7" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--diagram-ink)" />
        </marker>
      </defs>
      <g fill="none" stroke="var(--diagram-ink)" strokeWidth={0.08} strokeLinecap="round" strokeLinejoin="round">
        {lines.map((o) => (
          <g key={o.id} {...handlers(o.id)}>
            {/* Wide invisible stroke makes thin lines easy to grab */}
            {interactive && <path d={pathOf(sample(o.points))} stroke="transparent" strokeWidth={0.7} />}
            <g stroke={o.id === selectedId ? 'var(--action)' : undefined}>
              <Line o={o} markerId={markerId} />
            </g>
          </g>
        ))}
      </g>
      {others.map((o) => {
        const selected = o.id === selectedId;
        const ring = selected ? <circle cx={o.x} cy={o.y} r={0.7} fill="none" stroke="var(--action)" strokeWidth={0.06} strokeDasharray="0.15 0.1" /> : null;
        let body: ReactNode = null;
        switch (o.kind) {
          case 'offence':
            body = (
              <>
                <circle cx={o.x} cy={o.y} r={0.46} fill="var(--diagram-offence-fill)" stroke="var(--diagram-offence)" strokeWidth={0.08} />
                <text x={o.x} y={o.y} dy="0.19" textAnchor="middle" fontSize={0.52} fontWeight={700} fill="var(--diagram-offence)">
                  {o.label}
                </text>
              </>
            );
            break;
          case 'defence':
            body = (
              <text x={o.x} y={o.y} dy="0.22" textAnchor="middle" fontSize={0.62} fontWeight={800} fill="var(--diagram-defence)">
                X<tspan fontSize={0.4} dy="0.1">{o.label}</tspan>
              </text>
            );
            break;
          case 'coach':
            body = (
              <>
                <rect x={o.x - 0.42} y={o.y - 0.42} width={0.84} height={0.84} rx={0.12} fill="var(--diagram-offence-fill)" stroke="var(--diagram-ink)" strokeWidth={0.07} />
                <text x={o.x} y={o.y} dy="0.19" textAnchor="middle" fontSize={0.5} fontWeight={700} fill="var(--diagram-ink)">
                  C
                </text>
              </>
            );
            break;
          case 'ball':
            body = <circle cx={o.x} cy={o.y} r={0.26} fill="var(--diagram-ball)" stroke="var(--diagram-ball-line)" strokeWidth={0.05} />;
            break;
          case 'cone':
            body = <path d={`M ${o.x} ${o.y - 0.36} L ${o.x + 0.3} ${o.y + 0.26} L ${o.x - 0.3} ${o.y + 0.26} Z`} fill="var(--diagram-cone)" stroke="var(--diagram-cone-line)" strokeWidth={0.04} strokeLinejoin="round" />;
            break;
          case 'text':
            body = (
              <text x={o.x} y={o.y} dy="0.16" textAnchor="middle" fontSize={0.48} fontWeight={600} fill="var(--diagram-ink)" stroke="var(--court-floor)" strokeWidth={0.18} paintOrder="stroke">
                {o.text}
              </text>
            );
            break;
        }
        return (
          <g key={o.id} {...handlers(o.id)}>
            {interactive && <circle cx={o.x} cy={o.y} r={0.6} fill="transparent" />}
            {ring}
            {body}
          </g>
        );
      })}
    </>
  );
}

/** Read-only court with objects. */
export function CourtSvg({ court, objects, className, svgRef, children, label }: {
  court: 'half' | 'full';
  objects: DiagramObject[];
  className?: string;
  svgRef?: Ref<SVGSVGElement>;
  children?: ReactNode;
  label?: string;
}) {
  return (
    <svg ref={svgRef} viewBox={viewBox(court)} className={className} role="img" aria-label={label ?? 'Court diagram'} style={{ fontFamily: 'system-ui, sans-serif' }}>
      <CourtLines court={court} />
      <DiagramObjects objects={objects} />
      {children}
    </svg>
  );
}
